import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';

/**
 * 343 (#937): a Host's share link — `<app>/h/<name>` opens the customer app showing only that host's cars. Shown on a
 * Host membership's page. The server decides who may see a link (an administrator any host's, a host their own; 404
 * otherwise, and the card then shows nothing); only an administrator may set the name. The QR code is drawn here from
 * the link text and downloaded as a PNG; nothing about it is stored.
 */
interface ShareLink {
  share_name: string | null;
  share_url: string | null;
}

export function ShareLinkCard({
  membershipId,
  membershipRole,
}: { membershipId: string; membershipRole: string }) {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const isHost = membershipRole === 'Host';
  const canEdit = holdsAnywhere(ability as never, 'update', 'Membership');
  const key = ['membership-share-link', membershipId];
  const link = useQuery({
    queryKey: key,
    queryFn: () => api.get<ShareLink>(`/memberships/${membershipId}/share-link`),
    enabled: isHost,
    retry: false,
  });
  const [name, setName] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setName(link.data?.share_name ?? '');
  }, [link.data?.share_name]);

  const url = link.data?.share_url ?? null;
  useEffect(() => {
    let live = true;
    setQr(null);
    if (url) {
      QRCode.toDataURL(url, { margin: 2, width: 512 }).then((data) => {
        if (live) setQr(data);
      });
    }
    return () => {
      live = false;
    };
  }, [url]);

  const save = useMutation({
    mutationFn: () =>
      api.put<ShareLink>(`/memberships/${membershipId}/share-name`, {
        share_name: name.trim() === '' ? null : name.trim(),
      }),
    onSuccess: (saved) => queryClient.setQueryData(key, saved),
  });

  if (!isHost || !link.data) return null;

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Share link</h2>
      <p className="mb-4 text-sm text-steel">
        Opens the customer app showing only this host's cars at the customer's campus. Customers
        keep seeing only these cars until they choose "Show all cars".
      </p>
      {canEdit && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <FormField
            label="Link name"
            name="share_name"
            hint="3–40 letters, digits or hyphens. Changing it stops the old link and QR code working."
          >
            <Input
              id="share_name"
              value={name}
              maxLength={40}
              onChange={(e) => {
                setName(e.target.value);
                save.reset();
              }}
            />
          </FormField>
          <Button
            type="button"
            aria-label="Save link name"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      )}
      {save.isError && (
        <p role="alert" className="mb-3 text-sm text-red-600">
          {(save.error as { message?: string }).message ?? 'Save failed. Try again.'}
        </p>
      )}
      {url ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <code data-testid="share-url" className="rounded bg-surface px-2 py-1 text-sm text-ink">
              {url}
            </code>
            <Button
              type="button"
              variant="ghost"
              aria-label="Copy link"
              onClick={() => {
                void navigator.clipboard?.writeText(url);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          {qr && (
            <div className="flex items-end gap-4">
              <img
                src={qr}
                alt={`QR code for ${url}`}
                width={160}
                height={160}
                className="rounded border border-hairline"
              />
              <a
                href={qr}
                download={`share-link-${link.data.share_name}.png`}
                className="text-sm text-primary underline"
              >
                Download QR (PNG)
              </a>
            </div>
          )}
        </div>
      ) : (
        <p data-testid="share-none" className="text-sm text-steel">
          {canEdit
            ? 'No share link yet. Choose a link name above.'
            : 'No share link yet. Ask an administrator to set one.'}
        </p>
      )}
    </Card>
  );
}
